import React from 'react'
import { Box, Typography } from '@mui/material'

import { HeroStat, MetricsCards } from '../../components/socialdash'
import { getRowPercentage } from '../../components/social-media/utils'
import type { EntityMetricValue } from '../../components/social-media/metrics-config'
import type { DateRange } from '../../components/social-media/types'
import type { PlatinumData } from '../../types/carbon/models/PlatinumData'
import type { UseSkyCulturalRelevanceResult } from '../../hooks/socialdash'
import { hasCommentDateFilter } from './keyMetricsCaption'

/**
 * The KPI tiles of the team dashboard grid (rows 1 and 2).
 *
 * PRESENTATIONAL, like the kit they are built from: every tile takes data the
 * page already fetched and returns pixels. They live in the pages layer, not
 * in `components/socialdash/`, because each one is a Sky business reading of
 * a number ("Positive sentiment" is the `positive` row of the sentiment chart),
 * and that opinion belongs next to the pages that hold it.
 */

/** A 0–1 fraction as a one-decimal percentage, or null when there is none. */
const pct = (v: number | null): string | null => (v !== null ? `${(v * 100).toFixed(1)}%` : null)

/**
 * Everything a row-2 arrangement may draw from. The page fetches it once and
 * hands it to whichever `kpiRow2` the department passes, so a department that
 * wants a different row 2 changes its own file and nothing here.
 */
export interface KpiRow2Context {
  /** Chart id → table, as returned by `useSkyCharts` for the standard charts. */
  chartData: Record<string, PlatinumData | null>
  chartsLoading: boolean
  /** Entity metrics from `useSkyEntityMetrics`. */
  metrics: Record<string, EntityMetricValue> | null
  metricsLoading: boolean
  totalEntities: number
  culturalRelevance: UseSkyCulturalRelevanceResult['culturalRelevance']
  /** The live comment-date filter; the Cultural Relevance tooltip qualifies itself on it. */
  commentDateRange: DateRange | null | undefined
}

export interface CoverageTileProps {
  /** Posts matching the entity half of the filter. */
  totalEntities: number
  /** Analysed comments matching the whole filter. */
  commentCount: number
  isLoading: boolean
}

/**
 * COVERAGE VOLUME: how many posts this page covers, with the analysed comment
 * count under it. Post count is the headline because every other row-1 tile
 * (reach, views, engagements) is a sum over those same posts.
 */
export const CoverageTile: React.FC<CoverageTileProps> = ({ totalEntities, commentCount, isLoading }) => (
  <HeroStat
    label="Coverage volume"
    value={isLoading ? null : totalEntities.toLocaleString()}
    caption={`post${totalEntities !== 1 ? 's' : ''} · ${commentCount.toLocaleString()} comment${commentCount !== 1 ? 's' : ''} analysed`}
    emptyCaption={isLoading ? 'loading' : 'no data'}
    data-testid="sky-product-metric-coverage"
    valueTestId="sky-product-metric-value-coverage"
  />
)

/** POSITIVE SENTIMENT %: the `positive` row of the sentiment chart's table. */
export const PositiveSentimentTile: React.FC<{ data: PlatinumData | null | undefined }> = ({ data }) => (
  <HeroStat
    label="Positive sentiment"
    value={pct(getRowPercentage(data, 'positive'))}
    caption="of comments"
    data-testid="sky-product-metric-positive-sentiment"
    valueTestId="sky-product-metric-value-positive-sentiment"
  />
)

/**
 * RELEVANT SENTIMENT %: the `positive` row of the sentiment chart filtered to
 * the comments coded Yes on Sky Relevant (`sentiment-relevant`, filter
 * `skyrelevant(1)`). The engine narrows the column base too, so this is
 * "positive % of relevant comments" — what Sky asked for (#1489).
 */
export const RelevantSentimentTile: React.FC<{ data: PlatinumData | null | undefined }> = ({ data }) => (
  <HeroStat
    label="Relevant sentiment"
    value={pct(getRowPercentage(data, 'positive'))}
    caption="positive · Sky Relevant comments"
    data-testid="sky-product-metric-relevant-sentiment"
    valueTestId="sky-product-metric-value-relevant-sentiment"
  />
)

/** ATTRIBUTED SENTIMENT %: as Relevant sentiment, over the comments coded Yes on Sky Attributed. */
export const AttributedSentimentTile: React.FC<{ data: PlatinumData | null | undefined }> = ({ data }) => (
  <HeroStat
    label="Attributed sentiment"
    value={pct(getRowPercentage(data, 'positive'))}
    caption="positive · Sky Attributed comments"
    data-testid="sky-product-metric-attributed-sentiment"
    valueTestId="sky-product-metric-value-attributed-sentiment"
  />
)

export interface CulturalRelevanceTileProps {
  culturalRelevance: UseSkyCulturalRelevanceResult['culturalRelevance']
  commentDateRange: DateRange | null | undefined
}

/** CULTURAL RELEVANCE %: the NormComp standard average, with its explanatory tooltip. */
export const CulturalRelevanceTile: React.FC<CulturalRelevanceTileProps> = ({
  culturalRelevance,
  commentDateRange,
}) => (
  <HeroStat
    label="Cultural Relevance"
    value={
      culturalRelevance?.standardAvg != null ? `${culturalRelevance.standardAvg.toFixed(1)}%` : null
    }
    caption="standard average"
    // 🔴 NAMES THE MISSING INPUT RATHER THAN SAYING "no data" (Kai,
    // 2026-08-18). The server returns null for this card only when NO
    // video in the population carries u35_pct, and U35 is manual-upload
    // only — it will never arrive from Sprinklr. A bare "no data" reads
    // as a loading failure someone should retry; naming the upload turns
    // the blank into the question it actually is.
    emptyCaption="U35 data not uploaded"
    data-testid="sky-product-metric-cultural-relevance"
    valueTestId="sky-product-metric-value-cultural-relevance"
    tooltip={
      culturalRelevance?.standardAvg != null ? (
        <Box sx={{ p: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            NormComp Score: {culturalRelevance.normComp?.toFixed(1) ?? '—'}
          </Typography>
          {/* 🔴 WHAT THE NUMBER IS, IN ENGLISH, ABOVE THE NUMBERS THAT
              MAKE IT. Added 2026-03-18 (234fa6d7) to the old
              SocialAnalyticsDashboard and LOST when that page was retired
              (1cf016297) and this one took over — the tooltip was ported
              with its three component rows and without the sentence that
              said what they compose into. Restored per #1198.

              It matters because "Cultural Relevance 62.4%" reads as a
              share of something. It is not: it is a RELATIVE index, and a
              single-post sample scores exactly 50 by construction (see
              culturalRelevanceNormalize — no spread means 0.5). Without
              this line nothing on screen says so.

              Italic + reduced opacity + a rule under it, as the original
              had: prose, not a datum, and visibly separated from the
              three figures below. The rule is white-on-dark because MUI's
              default tooltip surface is dark in BOTH themes. */}
          <Typography
            variant="caption"
            component="div"
            sx={{
              mt: 0.5,
              mb: 0.5,
              pb: 0.5,
              fontStyle: 'italic',
              opacity: 0.85,
              borderBottom: '1px solid rgba(255,255,255,0.25)',
            }}
          >
            A relative 0–100 composite index created by min–max normalising Engagement,
            Discovery and Sentiment within the filtered sample and averaging the results.
          </Typography>
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
            ME (Engagement): {culturalRelevance.components.me?.toFixed(1) ?? '—'}%
          </Typography>
          <Typography variant="caption" component="div">
            MD (Discovery U35): {culturalRelevance.components.md?.toFixed(1) ?? '—'}%
          </Typography>
          <Typography variant="caption" component="div">
            MS (Sentiment): {culturalRelevance.components.ms?.toFixed(1) ?? '—'}%
          </Typography>
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
            {culturalRelevance.videoCount} post
            {culturalRelevance.videoCount !== 1 ? 's' : ''} with complete data
          </Typography>
          {/* 🔴 THE ONLY PARTIALLY-FILTERED TILE ON THE STRIP, so it needs
              a sentence the caption cannot give it. Under a comment-date
              filter the MS third genuinely narrows —
              buildEntityMeaningfulSentimentDB applies the same
              `r.source_date` predicate — while ME and MD come from the
              metrics aggregation over the post population and do not. So
              the tile MOVES, which is exactly why a reader would assume
              all three components moved with it. Named here rather than in
              the caption because it is a fact about this composite, not
              about the strip. */}
          {hasCommentDateFilter(commentDateRange) && (
            <Typography
              variant="caption"
              component="div"
              sx={{ mt: 0.5, fontStyle: 'italic', opacity: 0.85 }}
            >
              Comment date filter applies to MS only — ME and MD are post properties.
            </Typography>
          )}
        </Box>
      ) : undefined
    }
  />
)

/**
 * Entity-metric tiles by catalog key. The tile's label is the catalog's
 * display name ("Engagement Rate", "Meaningful ER"), which travels with the
 * value from the server; a key with no data still draws a "—" tile so the grid
 * keeps its shape.
 */
export const EntityMetricTiles: React.FC<{
  keys: string[]
  metrics: Record<string, EntityMetricValue> | null
  isLoading: boolean
}> = ({ keys, metrics, isLoading }) => (
  <MetricsCards keys={keys} metrics={metrics} isLoading={isLoading} renderEmpty />
)

/**
 * The standard row 2: POSITIVE SENTIMENT | RELEVANT SENTIMENT | ATTRIBUTED
 * SENTIMENT | ENGAGEMENT RATE + MEANINGFUL ER. The last slot is two tiles side
 * by side.
 */
export const DefaultKpiRow2: React.FC<KpiRow2Context> = (ctx) => (
  <>
    <PositiveSentimentTile data={ctx.chartData['sentiment']} />
    <RelevantSentimentTile data={ctx.chartData['sentiment-relevant']} />
    <AttributedSentimentTile data={ctx.chartData['sentiment-attributed']} />
    <EntityMetricTiles
      keys={['engagement_rate_pct', 'meaningful_er_pct']}
      metrics={ctx.metrics}
      isLoading={ctx.metricsLoading}
    />
  </>
)
